"use client";

import {createGtmDataLayerEmitter, createSegmentClientEmitter, type DataLayerEmitter, type SegmentTrackEmitter} from "./analytics.emitters";
import {
    type CanonicalAnalyticsEvent,
    CanonicalAnalyticsEventName,
    mapCanonicalEventToGtmPayloads,
    mapCanonicalEventToSegmentTrack,
} from "./analytics.events";
import {
    PropertyLoadMorePayload,
    PropertyShufflePayload,
    PropertyTagClickPayload,
    SearchResultsEventPayload,
    SimpleSearchEventPayload,
    ViewItemPayload,
} from "./analytics.types";

export type {
    EcommerceItem,
    InquiryFormOpenPayload,
    InquiryFormSubmitBookingSnapshot,
    InquiryFormSubmitPayload,
    InquiryFormValidationErrorPayload,
    OurPropertySectionType,
    PropertyLoadMorePayload,
    PropertyShufflePayload,
    PropertyTagClickPayload,
    PurchaseEventPayload,
    QuoteRequestedPayload,
    SearchResultsEventPayload,
    SimpleSearchEventPayload,
    ViewItemPayload,
} from "./analytics.types";

export class AnalyticsServiceClass {
    constructor(
        private readonly dataLayerEmitter: DataLayerEmitter = createGtmDataLayerEmitter(),
        private readonly segmentTrackEmitter: SegmentTrackEmitter = createSegmentClientEmitter(),
    ) {}

    protected push(data: Record<string, unknown>): void {
        this.dataLayerEmitter.push(data);
    }

    protected trackCanonicalEvent(event: CanonicalAnalyticsEvent): void {
        for (const payload of mapCanonicalEventToGtmPayloads(event)) {
            this.push(payload);
        }

        const segmentTrack = mapCanonicalEventToSegmentTrack(event);
        this.segmentTrackEmitter.track(segmentTrack.eventName, segmentTrack.properties);
    }

    trackSearchResults(payload: SearchResultsEventPayload): void {
        try {
            this.trackCanonicalEvent({name: CanonicalAnalyticsEventName.SearchResultsViewed, properties: payload});

            if (!payload.has_results) {
                this.trackCanonicalEvent({name: CanonicalAnalyticsEventName.SearchResultsEmpty, properties: payload});
            }
        } catch (err) {
            console.warn("[AnalyticsService] trackSearchResults failed:", err);
        }
    }

    trackSimpleSearch(payload: SimpleSearchEventPayload): void {
        try {
            this.trackCanonicalEvent({name: CanonicalAnalyticsEventName.SearchSubmitted, properties: payload});
        } catch (err) {
            console.warn("[AnalyticsService] trackSimpleSearch failed:", err);
        }
    }

    trackViewItem(payload: ViewItemPayload): void {
        try {
            this.trackCanonicalEvent({
                name: CanonicalAnalyticsEventName.RentalViewed,
                properties: {
                    unit_id: payload.item_id,
                    unit_name: payload.item_name,
                    value: payload.price,
                },
            });
        } catch (err) {
            console.warn("[AnalyticsService] trackViewItem failed:", err);
        }
    }

    trackPropertyShuffle(payload: PropertyShufflePayload): void {
        try {
            this.trackCanonicalEvent({name: CanonicalAnalyticsEventName.PropertyResultsShuffled, properties: payload});
        } catch (err) {
            console.warn("[AnalyticsService] trackPropertyShuffle failed:", err);
        }
    }

    trackPropertyTagClick(payload: PropertyTagClickPayload): void {
        try {
            this.trackCanonicalEvent({name: CanonicalAnalyticsEventName.PropertyTagClicked, properties: payload});
        } catch (err) {
            console.warn("[AnalyticsService] trackPropertyTagClick failed:", err);
        }
    }

    trackPropertyLoadMore(payload: PropertyLoadMorePayload): void {
        try {
            this.trackCanonicalEvent({name: CanonicalAnalyticsEventName.PropertyResultsLoadMoreClicked, properties: payload});
        } catch (err) {
            console.warn("[AnalyticsService] trackPropertyLoadMore failed:", err);
        }
    }
}

export type AnalyticsServiceInstance = AnalyticsServiceClass;

export function AnalyticsService(): AnalyticsServiceInstance {
    return new AnalyticsServiceClass();
}
